import React from 'react';

const formatNum = (n) => { if (!n) return '-'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(n>=1e4?0:1)+'K'; return String(n); };
const formatPct = (v) => v != null && v !== '' ? (parseFloat(v) * 100).toFixed(1) + '%' : '-';
const tierLabel = (t) => {
  if (t === 1) return { text: 'T1', cls: 'bg-amber-500/20 text-amber-400' };
  if (t === 2) return { text: 'T2', cls: 'bg-zinc-400/20 text-zinc-300' };
  if (t === 3) return { text: 'T3', cls: 'bg-orange-800/20 text-orange-400' };
  return { text: '--', cls: 'bg-zinc-800 text-zinc-600' };
};
const bssColor = (s) => { if (!s) return 'text-zinc-600'; if (s >= 4) return 'text-green-400'; if (s >= 3) return 'text-yellow-400'; return 'text-red-400'; };

const TalentCompare = ({ talents, onClose }) => {
  if (!talents || talents.length < 2) return null;

  // Find best value in each row for highlighting
  const bestOf = (key, opts = {}) => {
    const { higher = true } = opts;
    let best = null;
    let bestVal = higher ? -Infinity : Infinity;
    talents.forEach(t => {
      const v = parseFloat(t[key]);
      if (isNaN(v)) return;
      if ((higher && v > bestVal) || (!higher && v < bestVal)) { bestVal = v; best = t.id; }
    });
    return best;
  };

  const DemRow = ({ label, field, color }) => {
    const bestId = bestOf(field);
    return (
      <tr className="border-b border-zinc-800/50">
        <td className="py-2 pr-4 text-zinc-500 text-xs whitespace-nowrap">{label}</td>
        {talents.map(t => (
          <td key={t.id} className="py-2 px-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden" style={{ maxWidth: 120 }}>
                <div className={`h-full rounded-full ${color || 'bg-nextide-turquoise'}`}
                  style={{ width: `${(parseFloat(t[field]) || 0) * 100}%` }} />
              </div>
              <span className={`text-xs font-medium ${t.id === bestId ? 'text-green-400' : 'text-zinc-300'}`}>
                {formatPct(t[field])}
              </span>
            </div>
          </td>
        ))}
      </tr>
    );
  };

  const StatRow = ({ label, field, format, higher = true }) => {
    const bestId = bestOf(field, { higher });
    return (
      <tr className="border-b border-zinc-800/50">
        <td className="py-2 pr-4 text-zinc-500 text-xs whitespace-nowrap">{label}</td>
        {talents.map(t => {
          const val = t[field];
          const display = format ? format(val) : (val || '-');
          return (
            <td key={t.id} className="py-2 px-3">
              <span className={`text-sm font-medium ${t.id === bestId ? 'text-green-400' : 'text-white'}`}>
                {display}
              </span>
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/85 flex items-start justify-center z-50 overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full mx-6 overflow-hidden"
        style={{ maxWidth: Math.min(350 * talents.length + 160, 1400) }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-5 border-b border-zinc-800 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold text-white">Compare Talent</h2>
            <p className="text-zinc-500 text-xs mt-0.5">{talents.length} creators side by side</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">x</button>
        </div>

        <div className="p-5 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="text-left pb-3 pr-4 text-zinc-500 text-xs font-medium w-32"></th>
                {talents.map(t => (
                  <th key={t.id} className="text-left pb-3 px-3 min-w-[200px]">
                    <div className="text-white font-semibold text-sm">{t.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierLabel(t.talent_tier).cls}`}>{tierLabel(t.talent_tier).text}</span>
                      <span className={`text-xs font-semibold ${bssColor(t.brand_safety_score)}`}>BSS {t.brand_safety_score ? parseFloat(t.brand_safety_score).toFixed(1) : '-'}</span>
                    </div>
                    {t.primary_category && <div className="text-zinc-500 text-xs mt-1 truncate max-w-[200px]">{t.primary_category}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Key Stats */}
              <tr><td colSpan={talents.length + 1} className="pt-4 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Key Stats</td></tr>
              <StatRow label="Tier" field="talent_tier" format={v => v ? `Tier ${v}` : '-'} higher={false} />
              <StatRow label="BSS" field="brand_safety_score" format={v => v ? parseFloat(v).toFixed(1) : '-'} />
              <StatRow label="Total Following" field="total_following" format={formatNum} />
              <StatRow label="Twitch CCV" field="twitch_avg_ccv" format={formatNum} />

              {/* Platform */}
              <tr><td colSpan={talents.length + 1} className="pt-5 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Platform</td></tr>
              <StatRow label="Twitch" field="twitch_followers" format={formatNum} />
              <StatRow label="YouTube" field="youtube_followers" format={formatNum} />
              <StatRow label="Instagram" field="instagram_followers" format={formatNum} />
              <StatRow label="TikTok" field="tiktok_followers" format={formatNum} />
              <StatRow label="Twitter/X" field="twitter_followers" format={formatNum} />

              {/* Demographics */}
              <tr><td colSpan={talents.length + 1} className="pt-5 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Audience Gender</td></tr>
              <DemRow label="Male" field="aud_male" color="bg-blue-400" />
              <DemRow label="Female" field="aud_female" color="bg-pink-400" />

              <tr><td colSpan={talents.length + 1} className="pt-5 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Audience Age</td></tr>
              <DemRow label="Under 18" field="aud_u18" color="bg-orange-400" />
              <DemRow label="18-34" field="aud_18_34" color="bg-nextide-turquoise" />
              <DemRow label="35+" field="aud_35_plus" color="bg-violet-400" />

              <tr><td colSpan={talents.length + 1} className="pt-5 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Audience Geography</td></tr>
              <DemRow label="USA" field="aud_usa" color="bg-blue-500" />
              <DemRow label="Canada" field="aud_canada" color="bg-red-500" />
              <DemRow label="UK" field="aud_uk" color="bg-indigo-400" />
              <DemRow label="Australia" field="aud_australia" color="bg-yellow-500" />

              <tr><td colSpan={talents.length + 1} className="pt-5 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Audience Race</td></tr>
              <DemRow label="Black/AA" field="aud_black" />
              <DemRow label="White" field="aud_white" />
              <DemRow label="Asian" field="aud_asian" />
              <DemRow label="Hispanic" field="aud_hispanic" />

              {/* Conflicts */}
              <tr><td colSpan={talents.length + 1} className="pt-5 pb-2 text-zinc-400 text-xs font-semibold uppercase tracking-wide">Conflicts</td></tr>
              <tr className="border-b border-zinc-800/50">
                <td className="py-2 pr-4 text-zinc-500 text-xs"></td>
                {talents.map(t => {
                  const conflicts = t.conflicts || [];
                  return (
                    <td key={t.id} className="py-2 px-3">
                      {conflicts.length === 0 ? (
                        <span className="text-green-400 text-xs">None</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {conflicts.map((c, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded text-xs">{c.name}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TalentCompare;
